import { PageableQuery } from '../models';
import { map, concatMap, catchError } from 'rxjs/operators';
import axios from 'axios-observable';
import { WorkspacePageable, Workspace } from './models';
import { of, forkJoin, throwError } from 'rxjs';
import { protectorAppId } from '../cropwisePlans/models';

export const baseUrl = process.env.REACT_APP_BASE_API_URL ?? 'http://localhost:8080';

const WORKSPACES_PAGE_SIZE = 50;

export const getWorkspaceOrganizationsPage = (_query: PageableQuery, workspaceId: string) => {
  const params = {
    page: _query.pageable.page,
    size: _query.pageable.size,
    attributes: 'workspaces',
    force_list_unexplicit_orgs: true,
    workspaces: workspaceId
  };

  return axios
    .get<WorkspacePageable>(`${baseUrl}/v2/orgs`, {
      params
    })
    .pipe(map(response => response.data));
};

export const getWorkspaceOrganizations = (_query: PageableQuery, workspaceId: string) => {
  const size = 10;
  return getWorkspaceOrganizationsPage({ pageable: { page: 0, size } }, workspaceId).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);

      const observables = pages.map(p =>
        getWorkspaceOrganizationsPage({ pageable: { page: p, size } }, workspaceId).pipe(concatMap(orgsPage => of(orgsPage.content)))
      );

      return forkJoin([of(content), ...observables]);
    }),
    map(orgs => ({ data: { content: orgs.flat() } }))
  );
};

type WorkspacePage = { content: Workspace[]; total_pages: number };

// P40-61939: /v2/workspaces lists every workspace but requires workspace:read on crn:system,
// which only a few system roles hold. Everyone else reads their own workspaces from the
// account-scoped endpoint.
export const SYSTEM_WORKSPACES_PATH = '/v2/workspaces';
export const ACCOUNT_WORKSPACES_PATH = '/v2/accounts/workspaces';

// A 403 on the system endpoint is a permission fact, not a transient error, so it cannot recover
// within a session. Remembering it stops the per-keystroke workspace search from re-paying the 403.
let fellBackToAccountWorkspaces = false;

export const resetWorkspacesEndpointFallback = () => {
  fellBackToAccountWorkspaces = false;
};

const getSystemWorkspacesPage = (page: number, search?: string) => {
  const params: Record<string, string | number> = { app_id: protectorAppId, page, size: WORKSPACES_PAGE_SIZE };
  if (search) params.name = search;
  return axios.get<WorkspacePage>(`${baseUrl}${SYSTEM_WORKSPACES_PATH}`, { params }).pipe(map(response => response.data));
};

const getSystemWorkspaces = (search?: string) =>
  getSystemWorkspacesPage(0, search).pipe(
    concatMap(({ content, total_pages }) => {
      if (total_pages <= 1) return of(content);
      const remainingPages = Array.from({ length: total_pages - 1 }, (_, index) => index + 1);
      const remaining = remainingPages.map(page => getSystemWorkspacesPage(page, search).pipe(map(pageData => pageData.content)));
      return forkJoin([of(content), ...remaining]).pipe(map(pages => pages.flat()));
    })
  );

// The account endpoint answers with cursor pagination (pageable.next_key, last) and no total_pages,
// but neither next_key nor page moves the cursor, so pages past the first are unreachable today.
// NOTE: one oversized page instead of a cursor loop; swap in the loop once Platform documents
// the cursor param. It also ignores app_id, so results are not Protector-scoped (P40-61939 q3).
const ACCOUNT_WORKSPACES_PAGE_SIZE = 500;

type AccountWorkspacePage = { content: Workspace[]; last: boolean };

const getAccountWorkspaces = (search?: string) => {
  const params: Record<string, string | number> = { size: ACCOUNT_WORKSPACES_PAGE_SIZE };
  if (search) params.name = search;
  return axios.get<AccountWorkspacePage>(`${baseUrl}${ACCOUNT_WORKSPACES_PATH}`, { params }).pipe(
    concatMap(response =>
      // Fail loud rather than hand back a silently truncated list; a partial dropdown reads as
      // "my workspace is not there", which is the bug this whole change exists to remove. Anything
      // other than an explicit last: true counts as truncated, including the field going missing.
      response.data.last === true ? of(response.data.content ?? []) : throwError(new Error('workspaces_truncated'))
    )
  );
};

export const getWorkspaces = (search?: string, canReadSystemWorkspaces = false) => {
  if (!canReadSystemWorkspaces || fellBackToAccountWorkspaces) {
    return getAccountWorkspaces(search);
  }

  return getSystemWorkspaces(search).pipe(
    catchError(error => {
      // The allowlist behind canReadSystemWorkspaces is an inference from role payloads, so it can
      // be wrong. The server's 403 is authoritative; take it and switch endpoints for the session.
      if (error?.response?.status !== 403) return throwError(error);
      fellBackToAccountWorkspaces = true;
      return getAccountWorkspaces(search);
    })
  );
};

export const getWorkspaceById = (workspaceId: string) =>
  axios.get<Workspace>(`${baseUrl}/v2/workspaces/${workspaceId}`).pipe(map(response => response.data));
